<template>
  <div :id="id" v-bind="$attrs" />
</template>

<script lang="ts">
import { PropType, defineComponent, markRaw } from 'vue';
import Quill from 'quill';

import { stripNbsp } from '@/utils/html';
import { Nullable } from '@/utils/types';

interface IQuillEditor {
  quill: Nullable<Quill>;
}

let Embed: any = Quill.import('blots/embed');

class Breaker extends Embed {
  static tagName = 'br';
  static blotName = 'breaker';
}

Quill.register(Breaker);
Quill.debug('error');

export default defineComponent({
  name: 'QuillEditor',

  props: {
    id: {
      type: String,
      required: true
    },
    modelValue: {
      type: String,
      default: ''
    },
    toolbarOptions: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    placeholder: {
      type: String,
      default: null
    },
    readonly: Boolean
  },

  emits: ['update:modelValue'],

  data: (): IQuillEditor => ({
    quill: null,
  }),

  watch: {
    modelValue (val) {
      if (!this.quill) {
        return;
      }

      if (val !== this.quill.root.innerHTML && !this.quill.hasFocus()) {
        this.setValue(val);
      }
    },
  },

  mounted () {
    this.initQuill();
  },

  beforeUnmount () {
    this.quill?.off('text-change', this.onInput);
    // The snow theme inserts its toolbar as a sibling before the editor div,
    // outside this component's root, so unmounting the editor (a `v-if` toggle)
    // left the toolbar behind and every remount stacked another one above it.
    (this.quill?.getModule('toolbar') as { container?: HTMLElement } | undefined)?.container?.remove();
  },

  methods: {
    initQuill () {
      this.quill = markRaw(new Quill(`#${this.id}`, {
        modules: {
          toolbar: this.toolbarOptions,
          keyboard: {
            bindings: {
              linebreak: {
                key: 'Enter',
                shiftKey: true,
                handler: function (range: {index: number}) {
                  (this as any).quill.insertEmbed(range.index, 'breaker', true, Quill.sources.USER);
                  (this as any).quill.setSelection(range.index + 1, Quill.sources.SILENT);
                  return false;
                },
              }
            },
          },
        },
        placeholder: this.placeholder,
        theme: 'snow',
        readOnly: this.readonly
      }));

      if (this.modelValue) {
        this.setValue(this.modelValue);
      }

      this.quill.on('text-change', this.onInput);
    },
    onInput () {
      this.$emit('update:modelValue', stripNbsp(this.quill?.getSemanticHTML()));
    },
    setValue (value: string) {
      if (this.quill) {
        const delta = this.quill.clipboard.convert({ html: value });
        this.quill.setContents(delta, 'silent');
      }
    },
  }
})
</script>

<style lang="scss">
@import '~quill/dist/quill.snow.css';
</style>
